import html2canvas from 'html2canvas';
import { sanitizeExportColors } from '../../../../../utils/exportColors';
import { jsPDF } from 'jspdf';

const EXPORT_SCALE = 2;

/** How far html2canvas's baseline sits below the browser's, in ems. */
const BASELINE_NUDGE_EM = '-0.45em';

/**
 * Lifts every line of type by that much on the copy being drawn.
 *
 * html2canvas sets a line's baseline from the content box's top and its own
 * font metrics, ignoring the half-leading the browser uses to place the glyphs
 * — about 0.45em too low with the Bangla faces these memos are set in. It is
 * not only centred headings: every line is drawn low, which is what pushed the
 * invocation over the memo's plate in the file while the page looked right.
 *
 * Only elements whose children are text get the lift, so it is applied once per
 * line of type and never compounds down a tree. It is a transform rather than
 * `top`, so an absolutely placed line (the watermark) keeps its own position.
 */
function liftType(root) {
    root.querySelectorAll('*').forEach((node) => {
        if (node.children.length) return;
        const hasText = [...node.childNodes].some((child) => child.nodeType === 3 && child.textContent.trim());
        if (!hasText) return;
        const already = node.style.transform && node.style.transform !== 'none' ? `${node.style.transform} ` : '';
        node.style.transform = `${already}translateY(${BASELINE_NUDGE_EM})`;
    });
}

/**
 * Re-fits a cloned sheet to itself.
 *
 * Lines first: each is set back to the size it is cut from and squeezed to the
 * room it has. Then the blocks, whose height depends on those lines: each is
 * released, measured, and scaled onto the room it was given. Pure DOM, so it
 * works on a copy React knows nothing about.
 */
function refit(root) {
    root.querySelectorAll('[data-fit-line]').forEach((node) => {
        const base = Number(node.dataset.fitSize);
        if (!Number.isFinite(base) || base <= 0) return;
        node.style.fontSize = `${base}px`;
        const room = node.parentElement ? node.parentElement.clientWidth : 0;
        const natural = node.scrollWidth;
        if (room && natural && natural > room) node.style.fontSize = `${(base * room) / natural}px`;
    });

    root.querySelectorAll('[data-fit-zone]').forEach((zone) => {
        const inner = zone.firstElementChild;
        const room = Number(zone.dataset.fitRoom);
        const floor = Number(zone.dataset.fitMin) || 0.4;
        if (!inner || !Number.isFinite(room) || room <= 0) return;
        inner.style.transform = 'none';
        const needed = inner.offsetHeight;
        if (!needed) return;
        const fit = needed <= room ? 1 : Math.max(floor, room / needed);
        inner.style.transform = fit === 1 ? 'none' : `scale(${fit})`;
    });
}

/**
 * Draws the sheet to a canvas at its natural size. The on-screen copy sits inside
 * a scaled wrapper, so the sheet is cloned out into an unscaled, off-screen box
 * first — what is captured is the same A4 sheet, not the shrunken preview.
 */
export async function captureInvoiceCanvas(sheet) {
    const width = sheet.offsetWidth;
    const clone = sheet.cloneNode(true);
    const wrapper = document.createElement('div');
    wrapper.setAttribute('aria-hidden', 'true');
    wrapper.style.cssText = `position:fixed;left:-20000px;top:0;width:${width}px;pointer-events:none;z-index:-1;background:#fff`;
    clone.style.transform = 'none';
    wrapper.appendChild(clone);
    document.body.appendChild(wrapper);

    try {
        await document.fonts.ready;
        // The sheet's fitted lines and blocks were measured on the page, which
        // may have been laid out before the Bangla face arrived. The file is
        // drawn from this copy, so the copy is measured again, with the face
        // loaded, and the sheet that is rasterised is the one that was fitted.
        refit(clone);
        // After the fitting, so a lifted line is never measured.
        liftType(clone);
        const height = Math.max(clone.scrollHeight, clone.offsetHeight);
        return await html2canvas(clone, {
            scale: EXPORT_SCALE,
            width,
            height,
            windowWidth: width,
            windowHeight: height,
            useCORS: true,
            logging: false,
            backgroundColor: '#ffffff',
            // html2canvas can't parse oklch/lab/color() — rewrite any to rgb first.
            onclone: (d) => sanitizeExportColors(d.documentElement),
        });
    } finally {
        wrapper.remove();
    }
}

/** A4 pages cut from the canvas; an invoice that runs long simply continues on page 2. */
export function canvasToPdfBlob(canvas) {
    const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const pageW = pdf.internal.pageSize.getWidth();
    const pageH = pdf.internal.pageSize.getHeight();
    const pagePx = Math.floor((pageH / pageW) * canvas.width);

    let offset = 0;
    let index = 0;
    while (offset < canvas.height) {
        if (index > 0) pdf.addPage();
        const slice = Math.min(pagePx, canvas.height - offset);
        const part = document.createElement('canvas');
        part.width = canvas.width;
        part.height = slice;
        part.getContext('2d').drawImage(canvas, 0, offset, canvas.width, slice, 0, 0, canvas.width, slice);
        pdf.addImage(part.toDataURL('image/jpeg', 0.95), 'JPEG', 0, 0, pageW, (slice / canvas.width) * pageW, undefined, 'FAST');
        offset += slice;
        index += 1;
    }
    return pdf.output('blob');
}

export const canvasToPngBlob = (canvas) => new Promise((resolve, reject) => {
    canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('png'))), 'image/png');
});
